/** SA-448 (spec §4.3): the viewport's pure arithmetic — fit, zoom about a
 * point, a bounded pan, and the smallest pan that reveals a rectangle. */
import type { Rect } from './mapTypes'

export interface Viewport { scale: number; x: number; y: number }
export interface Size { w: number; h: number }

export const SCALE_MIN = 0.4
export const SCALE_MAX = 2

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export function fitScale(content: Size, viewportWidth: number): number {
  return content.w > 0 && viewportWidth > 0 ? Math.min(1, viewportWidth / content.w) : 1
}

export function fitHeight(content: Size, viewportWidth: number, maxHeight: number): number {
  return Math.min(content.h * fitScale(content, viewportWidth), maxHeight)
}

export function fit(content: Size, view: Size): Viewport {
  const scale = fitScale(content, view.w)
  const scaled = content.w * scale
  return { scale, x: scaled < view.w ? (view.w - scaled) / 2 : 0, y: 0 }
}

export function clampPan(vp: Viewport, content: Size, view: Size): Viewport {
  const w = content.w * vp.scale, h = content.h * vp.scale
  return {
    scale: vp.scale,
    x: clamp(vp.x, Math.min(0, view.w - w), Math.max(0, view.w - w)),
    y: clamp(vp.y, Math.min(0, view.h - h), Math.max(0, view.h - h)),
  }
}

export function zoomAt(vp: Viewport, factor: number, at: { x: number; y: number }, content: Size, view: Size): Viewport {
  const scale = clamp(vp.scale * factor, SCALE_MIN, SCALE_MAX)
  const k = scale / vp.scale
  return clampPan({ scale, x: at.x - (at.x - vp.x) * k, y: at.y - (at.y - vp.y) * k }, content, view)
}

export function panBy(vp: Viewport, dx: number, dy: number, content: Size, view: Size): Viewport {
  return clampPan({ ...vp, x: vp.x + dx, y: vp.y + dy }, content, view)
}

/** The smallest pan that brings `rect` (content coordinates) inside the view. */
export function reveal(vp: Viewport, rect: Rect, content: Size, view: Size): Viewport {
  const left = rect.x * vp.scale + vp.x, right = (rect.x + rect.w) * vp.scale + vp.x
  const top = rect.y * vp.scale + vp.y, bottom = (rect.y + rect.h) * vp.scale + vp.y
  const dx = left < 0 ? -left : right > view.w ? view.w - right : 0
  const dy = top < 0 ? -top : bottom > view.h ? view.h - bottom : 0
  return panBy(vp, dx, dy, content, view)
}
